/**
 * Configuración → «Cuenta y número» (SPEC-estadisticas-conexion §B.7.4): el transporte, la marca
 * frenada, los restos del modo simulado (sólo en twilio), el asistente «Buscar en Twilio →
 * Conectar», el diagnóstico de la conexión, la guía de la consola de Twilio, los links acortados
 * (Link Shortening, que se prende acá después de configurar el dominio en la consola; antes estaba en
 * «Envío»), la prueba con el Sandbox, la baja automática y, al final, lo que Twilio le cobra a Deenex: el saldo de la cuenta de
 * Twilio y sus alertas de gasto (antes estaban arriba de todo en «Saldo y costos», mezclados con el
 * saldo de la marca).
 *
 * El diagnóstico vive acá (y no en cada tarjeta) porque lo escriben dos: el asistente, que trae uno
 * nuevo al conectar, y el panel, que lo pide y lo refresca. La guía lo lee para marcar lo hecho.
 *
 * El Auth Token es un secreto: nunca se precarga (el backend ni lo devuelve), se escribe en un
 * input `password` y sale del estado de React apenas se usa (ver `AsistenteConexion`).
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2, OctagonAlert } from "lucide-react";
import type { CuentaPublica, Diagnostico } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { frenoSuperado } from "../../logic/conexion";
import { ModalMotivo } from "../../components/ModalMotivo";
import { Tarjeta } from "../../components/Tarjeta";
import { AsistenteConexion } from "../../components/conexion/AsistenteConexion";
import { PanelDiagnostico } from "../../components/conexion/PanelDiagnostico";
import { GuiaConsola } from "../../components/conexion/GuiaConsola";
import { PruebaSandbox } from "../../components/conexion/PruebaSandbox";
import { ReanudarTrasPrueba } from "../../components/conexion/ReanudarTrasPrueba";
import { TarjetaRestosSimulados } from "../../components/conexion/TarjetaRestosSimulados";
import { TarjetaSaldoTwilio } from "../../components/estadisticas/TarjetaSaldoTwilio";
import { TarjetaAlertasGasto } from "../../components/estadisticas/TarjetaAlertasGasto";
import { TarjetaLinkShortening } from "./TarjetaLinkShortening";
import { fechaHora } from "../../../campana-nueva/utils/format";

export default function SeccionCuenta() {
  const { cuenta, modo } = useCuentaOficial();
  const [diagnostico, setDiagnostico] = useState<Diagnostico | null>(null);
  const [tokenAbierto, setTokenAbierto] = useState(false);
  const alDiagnostico = useCallback((d: Diagnostico) => setDiagnostico(d), []);
  if (!cuenta) return null;
  const simulado = modo === "simulated";

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm flex items-center gap-3 flex-wrap">
        <span className="text-sm text-slate-600">Transporte:</span>
        {simulado
          ? <span className="text-sm font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">Simulado</span>
          : <span className="text-sm font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">Twilio (real)</span>}
        <span className="text-xs text-slate-400">Esto lo define el servidor (WA_OFICIAL_TRANSPORT); no se cambia desde acá.</span>
      </div>

      {cuenta.frenada?.activa && <TarjetaMarcaFrenada cuenta={cuenta} />}
      {!simulado && <TarjetaRestosSimulados />}
      <AsistenteConexion tokenAbierto={tokenAbierto} onTokenAbierto={setTokenAbierto} onDiagnostico={alDiagnostico} />
      <PanelDiagnostico diagnostico={diagnostico} onDiagnostico={alDiagnostico}
        onCambiarToken={() => { setTokenAbierto(true); window.scrollTo?.({ top: 0, behavior: "smooth" }); }} />
      <GuiaConsola diagnostico={diagnostico} />
      <TarjetaLinkShortening />
      {!simulado && <PruebaSandbox />}
      {!simulado && <TarjetaOptOut />}
      <TarjetaSaldoTwilio />
      <TarjetaAlertasGasto simulado={simulado} />
    </div>
  );
}

// ── Baja automática (Advanced Opt-Out) ───────────────────────────────────────────────────────

/**
 * Confirmación de que el Messaging Service tiene la baja por palabra BAJA. Se exporta porque la
 * alerta `advanced_opt_out` del Resumen lleva a «Bajas y respuestas» y ahí también se muestra.
 */
export function TarjetaOptOut() {
  const { cuenta, recargar } = useCuentaOficial();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [marcado, setMarcado] = useState(!!cuenta?.config?.advancedOptOutConfirmado);
  useEffect(() => { setMarcado(!!cuenta?.config?.advancedOptOutConfirmado); }, [cuenta?.config?.advancedOptOutConfirmado]);
  if (!cuenta) return null;

  const cambiar = async (v: boolean) => {
    if (guardando) return;
    setGuardando(true);
    setError("");
    setMarcado(v);
    try {
      const l = leerRespuesta(await oficialApi.actualizarConfig({ advancedOptOutConfirmado: v }));
      if (!l.ok) { setError(textoError(l)); setMarcado(!v); return; }
      await recargar();
    } catch (e) {
      setError(textoError(leerError(e)));
      setMarcado(!v);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Tarjeta titulo="Baja automática de Twilio (Advanced Opt-Out)">
      <p className="text-sm text-slate-700">
        En Twilio Console → Messaging → Services → <span className="font-mono">{cuenta.messagingServiceSid || "tu servicio (MG…)"}</span> → Opt-Out Management:
        activá Advanced Opt-Out con la palabra BAJA en español.
      </p>
      <label className="mt-3 flex items-center gap-2 text-sm text-slate-800">
        <input type="checkbox" checked={marcado} disabled={guardando} onChange={(e) => void cambiar(e.target.checked)}
          className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
        Ya lo activé
        {guardando && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </label>
      {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
    </Tarjeta>
  );
}

// ── Marca frenada ────────────────────────────────────────────────────────────────────────────

/**
 * La marca frenada, con su motivo y «Reanudar la marca». Si el freno es por `cumplimiento` y una prueba del Sandbox
 * salió después (`frenoSuperado`), en vez del botón de siempre se ofrece reanudar con el motivo cargado y condicionado
 * a este freno (`ReanudarTrasPrueba`).
 */
function TarjetaMarcaFrenada({ cuenta }: { cuenta: CuentaPublica }) {
  const { recargar } = useCuentaOficial();
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState("");
  const superado = frenoSuperado(cuenta);

  const reanudar = async (motivo: string) => {
    setOcupado(true);
    setError("");
    try {
      const l = leerRespuesta(await oficialApi.reanudarMarca(motivo));
      if (!l.ok) { setError(textoError(l)); return; }
      setAbierto(false);
      await recargar();
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
      <div className="flex items-start gap-3">
        <OctagonAlert size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-1">
          <p className="text-sm font-semibold text-red-800">La marca está frenada</p>
          <p className="text-sm text-red-700">{cuenta.frenada.motivo || "Sin motivo informado."}</p>
          <p className="text-xs text-red-700/80">
            {cuenta.frenada.codigo ? `Código ${cuenta.frenada.codigo} · ` : ""}{fechaHora(cuenta.frenada.at)}
          </p>
          {error && <p role="alert" className="text-sm text-red-800 font-medium">{error}</p>}
        </div>
        {!superado && (
          <button type="button" onClick={() => setAbierto(true)}
            className="rounded-xl bg-white border border-red-200 px-3.5 py-2 text-sm font-semibold text-red-700 hover:bg-red-100">
            Reanudar la marca
          </button>
        )}
      </div>
      <div className="mt-3 empty:hidden"><ReanudarTrasPrueba superado={superado} /></div>
      {abierto && (
        <ModalMotivo
          titulo="Reanudar la marca"
          descripcion="Contá qué revisaste. Las campañas frenadas no se reanudan solas: hay que reanudarlas una por una."
          confirmar="Reanudar"
          onConfirmar={(m) => void reanudar(m)}
          onCancelar={() => setAbierto(false)}
          busy={ocupado}
        />
      )}
    </div>
  );
}
